import { get } from 'lodash';
import { createSelector } from 'reselect';
import { ProductType } from '../../enums';

import { getEntities, getErrors, getQueries, selectSearchObj } from '../../global-selectors';
import { findQueryByRouteMatch } from '../../utils/query';

export const selectBootstrapConfig = createSelector(
  getEntities,
  entities => get(entities, 'bootstrap', null)
);

export const selectBootstrapLoading = createSelector(
  getQueries,
  getEntities,
  (queries, entities) => {
    const [, result] = findQueryByRouteMatch(queries, 'bootstrap') || [];
    return get(result, 'isPending', false) || get(entities, 'bootstrapping');
  }
);

export const selectBootstrapErrors = createSelector(
  getErrors,
  (errors) => {
    const [, result] = findQueryByRouteMatch(errors, 'bootstrap') || [];

    if (get(result, 'responseBody.status') < 400) {
      return null;
    }

    return get(result, 'responseBody', null);
  }
);

export const selectSubscriberConfig = createSelector(
  getEntities,
  entities => get(entities, 'subscriber', {})
);

export const selectSubscriptionsEntities = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions', {})
);

export const selectDisneySubscription = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions.disneySubscription', undefined)
);

export const selectSubscriptionList = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions.all', [])
);

export const selectLatestSubscription = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions.latestSubscription', {})
);

export const selectIsBuySubscriptions = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions.buySubscriptions', false)
);

export const selectAddOnSubscriptions = createSelector(
  getEntities,
  entities => get(entities, 'subscriptions.addOns', [])
);

export const selectProductsEntities = createSelector(
  getEntities,
  entities => get(entities, 'products.list', [])
);

export const selectClientProducts = createSelector(
  getEntities,
  entities => get(entities, 'bootstrap.products', [])
);

export const selectProductTypePlan = createSelector(
  selectProductsEntities,
  products => {
    return products?.filter(p => p.productType === ProductType.PLAN && p.hidden !== true);
  }
);

export const selectProductTypeAddOn = createSelector(
  selectProductsEntities,
  products => {
    return products?.filter(p => p.productType === ProductType.ADD_ON && p.hidden !== true)
  }
);

export const selectCouponApply = createSelector(
  getEntities,
  entities => get(entities, 'couponApply', {})
);

export const selectClientShortCode = createSelector(
  getEntities,
  entities => get(entities, 'bootstrap.shortCode')
);

export const selectRecaptchaKey = createSelector(
  getEntities,
  entities => get(entities, 'bootstrap.recaptchaKey')
);

export const selectGetTransactionLoading = createSelector(
  getQueries,
  (queries) => {
    const [, result] = findQueryByRouteMatch(queries, 'transactions') || [];
    return get(result, 'isPending', false);
  }
);

export const selectGetTranslationsLoading = createSelector(
  getQueries,
  (queries) => {
    const [, result] = findQueryByRouteMatch(queries, 'translations') || [];
    return get(result, 'isPending', false);
  }
);

export const selectReturnURLPath = createSelector(
  selectSearchObj,
  (searchObj) => {
    return get(searchObj, 'return_url');
  }
);

export const selectInvoicesEntities = createSelector(
  getEntities,
  (entities) => {
    return get(entities, 'invoices', []);
  }
);

export const selectInvoice = createSelector(
  getEntities,
  entities => get(entities, 'invoice', {})
);


export const selectSingleSubscriptionClient = createSelector(
  selectBootstrapConfig,
  bootstrapConfig => {
    return bootstrapConfig.singleSubscription;
  }
);

export const selectExchangeRate = createSelector(
  selectBootstrapConfig,
  bootstrapConfig => {
    return bootstrapConfig.exchangeRate;
  }
);

export const selectTotalPriceSummary = createSelector(
  getEntities,
  entities => get(entities, 'finalSummaryPrice', 0)
);
